import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useElementTracking } from "../../../analytics/analytics";

import type { ElementTracking } from "../../../analytics/analytics";
import type { FC } from "react";
import type { FieldValues, Path, PathValue } from "react-hook-form";

const CeRadioOption: FC<
  {
    option: { value: string; label: string };
    name: string;
  } & ElementTracking
> = ({ option: { label, value }, name, analyticsCategory }) => {
  // Annotating the label as it's clickable and this element uses the Controller.
  const analyticsProps = useElementTracking(analyticsCategory, name, "radio", value);
  return (
    <FormControlLabel
      key={value}
      value={value}
      control={<Radio />}
      label={label}
      {...analyticsProps}
    />
  );
};

export const CeRadio = <TFieldValues extends FieldValues>({
  label,
  name,
  options,
  analyticsCategory,
}: {
  label: string;
  name: Path<TFieldValues>;
  options: { value: PathValue<TFieldValues, Path<TFieldValues>>; label: string }[];
} & ElementTracking): React.JSX.Element => {
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl>
          <FormLabel>{label}</FormLabel>
          <RadioGroup row {...field}>
            {options.map((option, i) => (
              <CeRadioOption
                key={i}
                option={option}
                name={name}
                analyticsCategory={analyticsCategory}
              />
            ))}
          </RadioGroup>
          {error && (
            <Typography variant="caption" color="error">
              {error.message}
            </Typography>
          )}
        </FormControl>
      )}
    />
  );
};
